import { API_BASE_URL } from "../../../config";
import React, { useState, useEffect, useCallback, useRef } from 'react';

// Vector 2026-10-09 tg_12634: กด "ค้าง" ได้จากส่วน "รายการขาย" ของหน้ารายงาน (ไม่ต้องไปหน้าจัดการค้าง)
// ใช้ endpoint เดิมของหน้าจัดการค้าง: POST credit-debts {msdUid, source:'ACCOUNTING'} (ค้างทั้งบิล) · DELETE credit-debts/{id} (ยกเลิก)
// รอบที่บัญชีตรวจแล้ว (verified) backend ปฏิเสธ 403 ทั้งเพิ่ม/ยกเลิก → ซ่อนปุ่ม แสดงแค่สถานะ
const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const errorText = async (res) => {
    const t = await res.text();
    try { const j = JSON.parse(t); if (j && j.error) return j.error; } catch (e) { /* ไม่ใช่ JSON */ }
    return t || `HTTP ${res.status}`;
};

const getToken = () => {
    try { return JSON.parse(localStorage.getItem('user') || 'null')?.token || null; } catch (e) { return null; }
};

export const useReportCredit = (reportCode) => {
    const [debtByMsd, setDebtByMsd] = useState({});
    const [busyMsd, setBusyMsd] = useState(null);
    // ref กันกดซ้ำก่อน state อัปเดต (คลิกรัว ๆ ในเฟรมเดียวกัน)
    const inFlight = useRef(false);

    const reload = useCallback(async () => {
        const token = getToken();
        if (!token || !reportCode) return;
        try {
            const res = await fetch(`${API_BASE_URL}/saledata/api/module/credit-debts?reportCode=${encodeURIComponent(reportCode)}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) return;
            const list = await res.json();
            const map = {};
            (Array.isArray(list) ? list : []).forEach(d => { if (d.status !== 'CANCELLED') map[d.msdUid] = d; });
            setDebtByMsd(map);
        } catch (e) {
            console.error('credit reload error:', e);
        }
    }, [reportCode]);

    useEffect(() => { reload(); }, [reload]);

    const run = async (msdUid, doFetch, failText) => {
        if (inFlight.current) return;
        const token = getToken();
        if (!token) return;
        inFlight.current = true;
        setBusyMsd(msdUid);
        try {
            const res = await doFetch(token);
            if (!res.ok) throw new Error(await errorText(res));
        } catch (e) {
            alert(`${failText}: ${e.message || ''}`);
        } finally {
            // โหลดสถานะจริงเสมอ (กรณี 409 อีกฝั่งทำไปก่อน) แล้วค่อยปลดล็อกปุ่ม
            await reload();
            inFlight.current = false;
            setBusyMsd(null);
        }
    };

    const addCredit = (sale, name) => {
        const who = name ? `"${name}"` : 'ที่ยังไม่ระบุร้าน (บัญชีย้ายร้านได้ที่หน้าจัดการยอดค้าง)';
        if (!window.confirm(`ค้างทั้งบิล ${who} ${fmt(sale.total)} บาท?`)) return;
        return run(sale.msdUid, (token) => fetch(`${API_BASE_URL}/saledata/api/module/credit-debts`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ msdUid: sale.msdUid, source: 'ACCOUNTING' }),
        }), 'ตั้งค้างไม่สำเร็จ');
    };

    const cancelCredit = (debt, name) => {
        if (!window.confirm(`ยกเลิกค้างของ "${name || debt.customerName || 'ไม่ทราบชื่อ'}" ${fmt(debt.amount)} บาท?`)) return;
        return run(debt.msdUid, (token) => fetch(`${API_BASE_URL}/saledata/api/module/credit-debts/${debt.id}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
        }), 'ยกเลิกค้างไม่สำเร็จ');
    };

    return { debtByMsd, busyMsd, addCredit, cancelCredit };
};

// เซลล์ "ค้าง" ต่อบิล: ชำระแล้ว · ค้าง ฿x + ยกเลิกค้าง · ปุ่ม ค้าง · (รอบ verified ซ่อนปุ่ม)
const SaleCreditCell = ({ sale, customerName, isVerified, credit }) => {
    const debt = credit.debtByMsd[sale.msdUid];
    const busy = credit.busyMsd === sale.msdUid;
    if (debt && debt.status === 'PAID') {
        return <span style={{ color: '#2d7a24', fontWeight: 700 }}>ชำระแล้ว</span>;
    }
    if (debt) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <span style={{ background: '#fdecea', color: '#b02a2a', borderRadius: '6px', padding: '2px 8px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    ค้าง ฿{fmt(debt.amount)}
                </span>
                {!isVerified && (
                    <button className="btc-btn btn-sm" style={{ backgroundColor: '#6c757d', padding: '4px 8px', fontSize: '12px' }}
                        onClick={() => credit.cancelCredit(debt, customerName)} disabled={busy}>
                        {busy ? '...' : 'ยกเลิกค้าง'}
                    </button>
                )}
            </div>
        );
    }
    if (isVerified || !(Number(sale.total) > 0)) return <span style={{ color: '#adb5bd' }}>-</span>;
    return (
        <button className="btc-btn btn-sm" style={{ backgroundColor: '#e05252', padding: '4px 10px', fontSize: '12px' }}
            onClick={() => credit.addCredit(sale, customerName)} disabled={busy}>
            {busy ? '...' : 'ค้าง'}
        </button>
    );
};

export default SaleCreditCell;
